import { useState, useEffect } from 'react';

interface ITableState {
    editRowIndex: number;
    editColumnIndex: number;
    tempEditColumnIndex: number;
    tableId: string;
}

class TableStateStore {
    private tableStates: Record<string, ITableState> = {};
    private listeners: Set<(tableId: string) => void> = new Set();

    getState(tableId: string): ITableState {
        return this.tableStates[tableId] || {
            editRowIndex: -1,
            editColumnIndex: -1,
            tempEditColumnIndex: -1,
            tableId
        };
    }

    updateState(tableId: string, updates: Partial<ITableState>) {

        Object.keys(this.tableStates).forEach(currentTableId => {
            if (currentTableId !== tableId) {
                this.tableStates[currentTableId] = {
                    ...this.tableStates[currentTableId],
                    editRowIndex: -1,
                    editColumnIndex: -1,
                    tempEditColumnIndex: -1
                };
            }
        });

        this.tableStates[tableId] = {
            ...this.getState(tableId),
            ...updates
        };

        this.listeners.forEach(listener => listener(tableId));
    }

    subscribe(listener: (tableId: string) => void) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    removeTable(tableId: string) {
        delete this.tableStates[tableId];
        this.listeners.forEach(listener => listener(tableId));
    }
}

const store = new TableStateStore();

export function useTableState(tableId: string) {
    const [state, setState] = useState<ITableState>(() => store.getState(tableId));

    useEffect(() => {
        const unsubscribe = store.subscribe((changedTableId) => {
            setState(store.getState(tableId));
        });

        return () => {
            unsubscribe();
            store.removeTable(tableId);
        };
    }, [tableId]);

    const updateState = (updates: Partial<ITableState>) => {
        store.updateState(tableId, updates);
    };

    return {
        state,
        updateState
    };
}